import { useCallback, useMemo } from 'react';

import { bind } from 'bind-event-listener';
import { di } from 'react-magnetic-di';

import { getDocument } from '@atlaskit/browser-apis';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import { type RovoInstrumentedError } from '@atlassian/conversation-assistant-utils/httpUtils';
import { ExperienceTracker as ExperienceTrackerBase } from '@atlassian/experience-tracker';
import type { ExperienceEvent, StopEvent } from '@atlassian/experience-tracker/ExperienceEvent';
import type {
	ExperienceTrackerAPI as ExperienceTrackerAPIBase,
	FailOptions,
} from '@atlassian/experience-tracker/ExperienceTracker';

import {
	isAuthOrPermissionError,
	isClientFetchError,
	isProductNotActiveError,
} from '../../common/utils/is-error';
import { logException } from '../../services/sentry';
import { useAnalytics } from '../analytics';

import {
	ExperienceName,
	ExperienceNamePostfix,
	SLOExclusionReason,
	type ExperienceNameType,
	type SLOExclusionReasonType,
} from './constants';
import { getOwningTeam } from './get-owning-team';
import { TabVisibilityTracker } from './tab-visibility-tracker';

type FailOptionsWithTeamName = FailOptions & {
	/**
	 * The sentry team name
	 */
	teamName?: string;
};

/**
 * A slighly augmented version of the ExperienceTrackerAPI interface to add the teamName attribute to the fail method
 * The attributes are added by the proxy handler below
 */
export interface ExperienceTrackerAPI extends ExperienceTrackerAPIBase {
	fail(options: FailOptionsWithTeamName): void;
}

const isRovoInstrumentedError = (error: any): error is RovoInstrumentedError =>
	error &&
	typeof error === 'object' &&
	'errorAttributes' in error &&
	typeof error.errorAttributes === 'object';

const isTerminalEvent = (event: ExperienceEvent): event is StopEvent =>
	event.action === 'taskSuccess' || event.action === 'taskFail' || event.action === 'taskAbort';

export class ExperienceTrackerHandler {
	private static instance: ExperienceTrackerHandler;
	private static labelStack: LabelStack = [];
	public experienceTracker: ExperienceTrackerAPI = new ExperienceTrackerBase();
	private sendAnalyticsEvent: ReturnType<typeof useAnalytics>['sendAnalyticsEvent'] | null = null;

	private tabVisibilityByExperience = new Map<string, TabVisibilityTracker>();

	constructor(dropEvents: boolean) {
		const doc = getDocument();
		if (doc && fg('rovo_chat_exp_tracking_tab_vis_metrics')) {
			bind(doc, {
				type: 'visibilitychange',
				listener: () => {
					const isHidden = doc.visibilityState === 'hidden';
					this.tabVisibilityByExperience.forEach((tracker) => {
						tracker.onVisibilityChange(isHidden);
					});
				},
			});
		}

		if (!dropEvents) {
			this.experienceTracker.subscribe((event) => {
				if (event.action === 'taskStart' && fg('rovo_chat_exp_tracking_tab_vis_metrics')) {
					this.tabVisibilityByExperience.set(event.name, new TabVisibilityTracker(doc));
				}

				let filteredErrorAttributes = {};
				let preventSendingLogException = false;
				// When non-null, converts taskFail → taskAbort so the error is excluded from SLO
				// failure counts but still recorded for anomaly detection purposes.
				// Gated by a feature flag so this behaviour can be toggled off as a kill switch.
				let sloExclusion: { reason: SLOExclusionReasonType } | null = null;
				const isErrorEvent = 'error' in event;

				// Skip SLO exclusion logic for the aiMateFilteredClientError experience itself
				// to avoid re-processing and potential infinite loops.
				const isFilteredClientErrorExperience = event.name === ExperienceName.FILTERED_CLIENT_ERROR;

				if (isErrorEvent && !isFilteredClientErrorExperience) {
					// Classify common non-service errors so they can be tracked separately from
					// real backend failures:
					//   - client fetch errors (e.g. "Failed to fetch") — request never reached Atlassian
					//   - product-not-active — HTTP 200 but SSE payload contains an errorCode
					//   - auth/permission — HTTP 401/403 (session expired, HIPAA, IP allowlist, etc.)
					if (fg('rovo_chat_exclude_client_errors_from_slo')) {
						if (isClientFetchError(event.error)) {
							preventSendingLogException = true;
							sloExclusion = { reason: SLOExclusionReason.CLIENT_FETCH_ERROR };
						} else if (isProductNotActiveError(event.error)) {
							preventSendingLogException = true;
							sloExclusion = { reason: SLOExclusionReason.PRODUCT_NOT_ACTIVE };
						} else if (isAuthOrPermissionError(event.error)) {
							preventSendingLogException = true;
							sloExclusion = { reason: SLOExclusionReason.AUTH_OR_PERMISSION_ERROR };
						}
					}

					if (event.attributes) {
						const { webSearchEnabled, deepResearchEnabled, thinkDeeperEnabled, fastModeEnabled } =
							event.attributes as Record<string, any>;

						if (webSearchEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								webSearchEnabled,
							};
						}

						if (deepResearchEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								deepResearchEnabled,
							};
						}

						if (fastModeEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								fastModeEnabled,
							};
						}

						if (thinkDeeperEnabled !== undefined) {
							filteredErrorAttributes = {
								...filteredErrorAttributes,
								thinkDeeperEnabled,
							};
						}
					}

					if (isRovoInstrumentedError(event.error)) {
						const errorAttributes = event.error.errorAttributes;

						// Remove undefined properties
						filteredErrorAttributes = Object.fromEntries(
							Object.entries(errorAttributes).filter(([, value]) => value !== undefined),
						);
					}

					const teamName = getOwningTeam(
						event.error.message ?? '',
						event.name as ExperienceNameType,
					);
					filteredErrorAttributes = {
						teamName, // We intentionally put this first so a `teamName` attribute can override this.
						...filteredErrorAttributes,
					};

					if (!preventSendingLogException) {
						const error =
							event.error instanceof Error ? event.error : new Error(JSON.stringify(event.error));

						const attributes = event.attributes as Record<string, any> | undefined;
						// this is added by `useExperienceTracker` below
						const touchpointSource = attributes?.touchpointSource;
						const teamName = attributes?.teamName;
						const { traceId: traceIdFromErrorObject, ...filteredErrorAttributesRest } =
							filteredErrorAttributes as RovoInstrumentedError['errorAttributes'];
						const altTraceId = attributes?.traceId || traceIdFromErrorObject;

						logException({
							exception: error,
							name: event.name,
							tags: {
								source: 'aiMateExperienceTracker',
								...filteredErrorAttributesRest,
								...(touchpointSource && {
									touchpointSource,
								}),
								...(teamName && {
									teamName,
								}),
								// Rename traceId to atlTraceId if present in attributes to distinguish from Sentry's Trace ID
								...(altTraceId && {
									altTraceId,
								}),
							},
						});
					}
				}

				if (isTerminalEvent(event)) {
					// the experience tracker already uses performance.now() to calculate the duration, so we can just piggyback on that,
					// we will loose some precision due to rounding of duration, but it's good enough for our purposes
					addSpanToAll(
						'custom',
						`exp-${event.name}`,
						ExperienceTrackerHandler.labelStack,
						event.startTime,
						event.startTime + event.duration,
					);
				}

				const tabVisibilityAttributes =
					isTerminalEvent(event) && fg('rovo_chat_exp_tracking_tab_vis_metrics')
						? (this.tabVisibilityByExperience.get(event.name)?.getAttributes(doc) ?? {})
						: {};

				// If the error should be excluded from SLO, report it as taskAbort instead of taskFail.
				// taskAbort events are not counted as failures in the SLO but are still recorded
				// so anomaly detectors can track spikes in these error types.
				const effectiveAction =
					sloExclusion && event.action === 'taskFail' ? 'taskAbort' : event.action;

				this.sendAnalyticsEvent?.(
					{
						eventType: 'operational',
						action: effectiveAction,
						actionSubject: 'ui',
						attributes: {
							task: event.name,
							taskId: event.id,
							startTime: event.startTime,
							...(isTerminalEvent(event) && {
								duration: event.duration,
							}),
							// Include error info on both taskFail and taskAbort (for excluded errors)
							// so analytics/anomaly detectors can distinguish error types.
							...(isErrorEvent && {
								error: event.error.message,
								...filteredErrorAttributes,
							}),
							// Surface why the error was excluded from the SLO so the raw analytics
							// event can be queried/grouped without joining to the synthetic experience.
							...(sloExclusion && { sloExclusionReason: sloExclusion.reason }),
							...event.attributes,
							...tabVisibilityAttributes,
						},
					},
					{
						removeDefaultTags: true,
					},
				);

				// When a client error is excluded from the main SLO (ie. converted to taskAbort),
				// track a separate experience so the volume of these errors can be monitored with its own SLO threshold.
				if (sloExclusion && isErrorEvent) {
					this.experienceTracker.start({
						name: ExperienceName.FILTERED_CLIENT_ERROR,
						id: ExperienceName.FILTERED_CLIENT_ERROR,
						startTime: event.startTime,
					});
					this.experienceTracker.fail({
						name: ExperienceName.FILTERED_CLIENT_ERROR,
						error: event.error,
						attributes: {
							// Preserve the original experience name, error, and exclusion reason for queryability
							originalTask: event.name,
							error: event.error.message,
							sloExclusionReason: sloExclusion.reason,
							...filteredErrorAttributes,
						},
					});
				}

				if (isTerminalEvent(event) && fg('rovo_chat_exp_tracking_tab_vis_metrics')) {
					this.tabVisibilityByExperience.delete(event.name);
				}
			});
		}
	}

	setSendAnalyticsEvent(
		sendAnalyticsEvent: ReturnType<typeof useAnalytics>['sendAnalyticsEvent'],
	): void {
		this.sendAnalyticsEvent = sendAnalyticsEvent;
	}

	static getInstance(): ExperienceTrackerHandler {
		if (!ExperienceTrackerHandler.instance) {
			const dropEvents = FeatureGates.getExperimentValue(
				'platform_ai_drop_instrumentation_tracking',
				'value',
				false,
			);
			ExperienceTrackerHandler.instance = new ExperienceTrackerHandler(dropEvents);
		}

		return ExperienceTrackerHandler.instance;
	}

	/**
	 * @internal For tests only.
	 */
	static resetInstance(): void {
		ExperienceTrackerHandler.instance = undefined as any;
	}

	static setLabelStack(labelStack: LabelStack): void {
		ExperienceTrackerHandler.labelStack = labelStack;
	}
}

type Params = {
	touchpointSource?: string;
};

export const useUFOLabelStackSetter = (): ((componentLabelStack: LabelStack) => void) => {
	return useCallback((componentLabelStack: LabelStack) => {
		ExperienceTrackerHandler.setLabelStack(componentLabelStack);
	}, []);
};

export const useAIMateExperienceTracker = (params?: Params): ExperienceTrackerAPI => {
	di(useAnalytics);
	const { sendAnalyticsEvent, mode: conversationAssistantUIMode } = useAnalytics();

	const getNameOverride = useCallback(
		(name: string) => {
			switch (conversationAssistantUIMode) {
				case 'mini-modal':
					return `${name}.${ExperienceNamePostfix.MINI_MODAL}`;
				default:
					return name;
			}
		},
		[conversationAssistantUIMode],
	);

	useMemo(() => {
		const experienceTracker = ExperienceTrackerHandler.getInstance();
		// this is not ideal, okay just because `sendAnalyticsEvent` is never change
		// since every hook instance will override the singleton sendAnalyticsEvent
		experienceTracker.setSendAnalyticsEvent(sendAnalyticsEvent);
	}, [sendAnalyticsEvent]);

	const experienceTrackerProxy = useMemo(() => {
		const proxyHandler = {
			get: (target: ExperienceTrackerAPI, prop: keyof ExperienceTrackerAPI) => {
				// Overriding the experienceTracker target methods to add additional attributes
				if (prop === 'fail') {
					return (failParams: Parameters<typeof target.fail>[0]) => {
						target.fail({
							...failParams,
							name: !!failParams.name ? getNameOverride(failParams.name) : failParams.name,
							attributes: {
								...failParams.attributes,
								touchpointSource: params?.touchpointSource,
								teamName: failParams.teamName,
							},
						});
					};
				}

				if (prop === 'succeed') {
					return (succeedParams: Parameters<typeof target.succeed>[0]) => {
						target.succeed({
							...succeedParams,
							name: !!succeedParams.name ? getNameOverride(succeedParams.name) : succeedParams.name,
							attributes: {
								...succeedParams.attributes,
								touchpointSource: params?.touchpointSource,
							},
						});
					};
				}

				if (prop === 'start') {
					return (startParams: Parameters<typeof target.start>[0]) => {
						target.start({
							...startParams,
							name: !!startParams.name ? getNameOverride(startParams.name) : startParams.name,
						});
					};
				}

				if (prop === 'abort') {
					return (abortParams: Parameters<typeof target.abort>[0]) => {
						target.abort({
							...abortParams,
							name: !!abortParams.name ? getNameOverride(abortParams.name) : abortParams.name,
						});
					};
				}

				if (prop === 'stopOnError') {
					return (stopOnErrorParams: Parameters<typeof target.stopOnError>[0]) => {
						target.stopOnError({
							...stopOnErrorParams,
							name: !!stopOnErrorParams.name
								? getNameOverride(stopOnErrorParams.name)
								: stopOnErrorParams.name,
						});
					};
				}

				return typeof target[prop] === 'function' ? target[prop].bind(target) : target[prop];
			},
		};

		return new Proxy(ExperienceTrackerHandler.getInstance().experienceTracker, proxyHandler);
	}, [params?.touchpointSource, getNameOverride]);

	return experienceTrackerProxy;
};
